import React, { useState, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  Alert,
  Divider,
  IconButton,
} from "@mui/material";

import {
  Email,
  ArrowBack,
  VerifiedUser,
  Security,
  School,
  ShieldOutlined,
  ArrowForward,
  LockOutlined,
} from "@mui/icons-material";

import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api/api";
import { AuthContext } from "../AuthContext";

const LoginAuthentication = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useContext(AuthContext);

  const email =
    location.state?.email || localStorage.getItem("auth_email");

  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showCode, setShowCode] = useState(true);

  const redirectUser = (user) => {
    const role = (user.user_type || user.role || "").toLowerCase();

    const dashboardRoutes = {
      owner: "/owner-dashboard",
      admin: "/newAdminDashboard",
      proprietor: "/proprietorDashboard",
      principal: "/principalDashboard",
      teacher: "/teacher-dashboard",
      student: "/student-dashboard",
      parent: "/parentsDashboard",
      registry: "/registerDashboard",
      vpi: "/vpiDashboard",
      vpa: "/newVpaDashboard",
      "it personel": "/tTPersonalDashboard",
      business_manager: "/businessManagerDashboard",
      sales_agent: "/sales-dashboard",
    };

    navigate(dashboardRoutes[role] || "/");
  };

  const verifyCode = async (e) => {
    e.preventDefault();

    if (!email) {
      setMessage("Your authentication email is missing. Please log in again.");
      return;
    }

    if (!code.trim()) {
      setMessage("Please enter the authentication code sent to your email.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await api.post(
        "/api/users/verify-authentication-code/",
        {
          email,
          code: code.trim(),
        }
      );

      const access = response.data.access;
      const refresh = response.data.refresh;

      if (!access || !refresh) {
        throw new Error("Authentication failed. Tokens are missing.");
      }

      localStorage.setItem("access", access);
      localStorage.setItem("refresh", refresh);

      window.dispatchEvent(new Event("user-logged-in"));

      const profile = await api.get("/api/user-profile/");
      const user = profile.data;

      setUser(user);
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.removeItem("auth_email");

      redirectUser(user);
    } catch (error) {
      console.error("Authentication verification failed:", error);

      setMessage(
        error.response?.data?.error ||
          error.response?.data?.detail ||
          error.message ||
          "Invalid authentication code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const styles = `
    .jana-auth-page {
      --auth-purple: #54218f;
      --auth-deep: #35105f;
      --auth-gold: #f4c542;
      --auth-gold-dark: #d7a81d;
      --auth-muted: #777184;
      position: relative;
      isolation: isolate;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      min-height: 100dvh;
      padding: 45px 22px;
      overflow: hidden;
      background:
        radial-gradient(ellipse at 0% 0%, rgba(84, 33, 143, 0.12), transparent 38%),
        radial-gradient(ellipse at 100% 100%, rgba(244, 197, 66, 0.15), transparent 35%),
        #faf8fd;
      font-family: inherit;
    }

    .jana-auth-page *,
    .jana-auth-page *::before,
    .jana-auth-page *::after {
      box-sizing: border-box;
    }

    .jana-auth-decoration {
      position: absolute;
      z-index: -1;
      width: 370px;
      height: 370px;
      border: 65px solid rgba(84, 33, 143, 0.045);
      border-radius: 50%;
      pointer-events: none;
    }

    .jana-auth-decoration-one {
      top: -205px;
      left: -125px;
    }

    .jana-auth-decoration-two {
      right: -225px;
      bottom: -190px;
      border-color: rgba(244, 197, 66, 0.15);
    }

    .jana-auth-layout {
      display: grid;
      grid-template-columns: minmax(0, 0.92fr) minmax(390px, 1.08fr);
      width: 100%;
      max-width: 1000px;
      min-height: 590px;
      overflow: hidden;
      border: 1px solid rgba(84, 33, 143, 0.12);
      border-radius: 27px;
      background: #ffffff;
      box-shadow: 0 30px 90px rgba(53, 16, 95, 0.13);
    }

    .jana-auth-brand-panel {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      min-width: 0;
      padding: 43px 37px 32px;
      overflow: hidden;
      background: linear-gradient(145deg, #35105f 0%, #54218f 60%, #6833a1 100%);
      color: #ffffff;
    }

    .jana-auth-brand-panel::before {
      content: "";
      position: absolute;
      right: -110px;
      bottom: 40px;
      width: 300px;
      height: 300px;
      border: 45px solid rgba(255, 255, 255, 0.055);
      border-radius: 50%;
      pointer-events: none;
    }

    .jana-auth-brand-panel::after {
      content: "";
      position: absolute;
      top: 40%;
      right: -100px;
      width: 210px;
      height: 210px;
      border-radius: 50%;
      background: rgba(244, 197, 66, 0.08);
      pointer-events: none;
    }

    .jana-auth-brand,
    .jana-auth-brand-content,
    .jana-auth-brand-footer {
      position: relative;
      z-index: 1;
    }

    .jana-auth-brand {
      display: flex;
      align-items: center;
      gap: 13px;
    }

    .jana-auth-brand-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      flex-shrink: 0;
      border-radius: 14px;
      background: var(--auth-gold);
      color: var(--auth-deep);
      font-size: 25px;
      box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
    }

    .jana-auth-brand-name {
      margin: 0;
      color: #ffffff;
      font-size: 14px;
      font-weight: 900;
      letter-spacing: 0.5px;
      line-height: 1.5;
    }

    .jana-auth-brand-subtitle {
      display: block;
      margin-top: 3px;
      color: rgba(255, 255, 255, 0.68);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 1.5px;
      text-transform: uppercase;
    }

    .jana-auth-brand-content {
      margin: 48px 0;
    }

    .jana-auth-brand-label {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 9px 12px;
      border: 1px solid rgba(244, 197, 66, 0.28);
      border-radius: 30px;
      background: rgba(244, 197, 66, 0.1);
      color: var(--auth-gold);
      font-size: 10px;
      font-weight: 850;
      letter-spacing: 1.3px;
      text-transform: uppercase;
    }

    .jana-auth-brand-content h1 {
      margin: 25px 0 18px;
      color: #ffffff;
      font-size: clamp(30px, 3.4vw, 42px);
      font-weight: 850;
      line-height: 1.2;
      letter-spacing: -1.2px;
    }

    .jana-auth-brand-content h1 span {
      color: var(--auth-gold);
    }

    .jana-auth-brand-content > p {
      margin: 0;
      color: rgba(255, 255, 255, 0.76);
      font-size: 13px;
      line-height: 2;
    }

    .jana-auth-security-list {
      display: grid;
      gap: 19px;
      margin-top: 33px;
    }

    .jana-auth-security-item {
      display: flex;
      align-items: center;
      gap: 13px;
    }

    .jana-auth-security-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 38px;
      height: 38px;
      flex-shrink: 0;
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 11px;
      background: rgba(255, 255, 255, 0.08);
      color: var(--auth-gold);
      font-size: 18px;
    }

    .jana-auth-security-item strong {
      display: block;
      margin-bottom: 4px;
      color: #ffffff;
      font-size: 12px;
      font-weight: 800;
    }

    .jana-auth-security-item span {
      display: block;
      color: rgba(255, 255, 255, 0.62);
      font-size: 10.5px;
      line-height: 1.6;
    }

    .jana-auth-brand-footer {
      padding-top: 22px;
      border-top: 1px solid rgba(255, 255, 255, 0.15);
      color: rgba(255, 255, 255, 0.58);
      font-size: 10px;
      line-height: 1.8;
    }

    .jana-auth-form-panel {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 0;
      padding: 48px 43px;
      background: #ffffff;
    }

    .jana-auth-form-content {
      width: 100%;
      max-width: 380px;
    }

    .jana-auth-mobile-brand {
      display: none;
    }

    .jana-auth-step {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 23px;
      color: var(--auth-purple);
      font-size: 10px;
      font-weight: 850;
      letter-spacing: 1.5px;
      text-transform: uppercase;
    }

    .jana-auth-step-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--auth-gold-dark);
      box-shadow: 0 0 0 4px rgba(244, 197, 66, 0.17);
    }

    .jana-auth-heading {
      margin: 0 0 12px;
      color: var(--auth-deep);
      font-size: 31px;
      font-weight: 900;
      line-height: 1.25;
      letter-spacing: -0.8px;
    }

    .jana-auth-description {
      margin: 0 0 28px;
      color: var(--auth-muted);
      font-size: 13px;
      line-height: 1.9;
    }

    .jana-auth-email {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      margin-bottom: 27px;
      padding: 15px;
      border: 1px solid #e9def7;
      border-radius: 13px;
      background: #faf7ff;
      overflow-wrap: anywhere;
    }

    .jana-auth-email-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      flex-shrink: 0;
      border-radius: 10px;
      background: #eee3fb;
      color: var(--auth-purple);
      font-size: 18px;
    }

    .jana-auth-email-label {
      display: block;
      margin-bottom: 4px;
      color: #8b8198;
      font-size: 10px;
      font-weight: 750;
      letter-spacing: 0.6px;
      text-transform: uppercase;
    }

    .jana-auth-email-value {
      display: block;
      color: var(--auth-deep);
      font-size: 12px;
      font-weight: 800;
      line-height: 1.65;
      overflow-wrap: anywhere;
    }

    .jana-auth-code-label {
      display: block;
      margin-bottom: 10px;
      color: #4b3b60;
      font-size: 12px;
      font-weight: 850;
    }

    .jana-auth-code-input .MuiOutlinedInput-root {
      min-height: 59px;
      border-radius: 13px;
      background: #ffffff;
      font-size: 17px;
      transition: box-shadow 0.25s ease;
    }

    .jana-auth-code-input .MuiOutlinedInput-notchedOutline {
      border-color: #e3d9ef;
    }

    .jana-auth-code-input .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
      border-color: #a68ac9;
    }

    .jana-auth-code-input .MuiOutlinedInput-root.Mui-focused {
      box-shadow: 0 0 0 4px rgba(84, 33, 143, 0.08);
    }

    .jana-auth-code-input .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: var(--auth-purple);
      border-width: 1.5px;
    }

    .jana-auth-code-input input {
      color: var(--auth-deep);
      font-weight: 800;
      letter-spacing: 3px;
    }

    .jana-auth-primary-button {
      min-height: 53px !important;
      border-radius: 12px !important;
      background: linear-gradient(110deg, #54218f, #35105f) !important;
      color: #ffffff !important;
      font-size: 13px !important;
      font-weight: 850 !important;
      text-transform: none !important;
      box-shadow: 0 9px 22px rgba(84, 33, 143, 0.2) !important;
      transition: transform 0.25s ease, box-shadow 0.25s ease !important;
    }

    .jana-auth-primary-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 13px 26px rgba(84, 33, 143, 0.28) !important;
    }

    .jana-auth-primary-button.Mui-disabled {
      background: #c9bed6 !important;
      color: #ffffff !important;
    }

    .jana-auth-back-button {
      min-height: 49px !important;
      margin-top: 12px !important;
      border: 1px solid #e4d9ef !important;
      border-radius: 12px !important;
      color: var(--auth-purple) !important;
      font-size: 12px !important;
      font-weight: 800 !important;
      text-transform: none !important;
    }

    .jana-auth-back-button:hover {
      border-color: var(--auth-purple) !important;
      background: #faf7ff !important;
    }

    .jana-auth-privacy {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 7px;
      margin-top: 23px;
      color: #8c8498;
      font-size: 10px;
      line-height: 1.7;
      text-align: center;
    }

    .jana-auth-privacy svg {
      flex-shrink: 0;
      color: #8c6ab1;
      font-size: 15px;
    }

    @media (max-width: 780px) {
      .jana-auth-page {
        padding: 25px 16px;
      }

      .jana-auth-layout {
        grid-template-columns: minmax(0, 1fr);
        max-width: 510px;
        min-height: auto;
        border-radius: 23px;
      }

      .jana-auth-brand-panel {
        display: none;
      }

      .jana-auth-form-panel {
        padding: 37px 28px;
      }

      .jana-auth-mobile-brand {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 36px;
      }

      .jana-auth-mobile-brand .jana-auth-brand-name {
        color: var(--auth-deep);
      }

      .jana-auth-mobile-brand .jana-auth-brand-subtitle {
        color: var(--auth-muted);
      }
    }

    @media (max-width: 420px) {
      .jana-auth-form-panel {
        padding: 29px 20px;
      }

      .jana-auth-heading {
        font-size: 28px;
      }

      .jana-auth-description {
        font-size: 12px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .jana-auth-primary-button,
      .jana-auth-code-input .MuiOutlinedInput-root {
        transition: none !important;
      }
    }
  `;

  return (
    <Box className="jana-auth-page">
      <style>{styles}</style>

      <div
        className="jana-auth-decoration jana-auth-decoration-one"
        aria-hidden="true"
      />

      <div
        className="jana-auth-decoration jana-auth-decoration-two"
        aria-hidden="true"
      />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
        className="jana-auth-layout"
      >
        {/* Branded welcome panel */}
        <Box className="jana-auth-brand-panel">
          <Box className="jana-auth-brand">
            <Box className="jana-auth-brand-icon">
              <School />
            </Box>

            <Box>
              <Typography className="jana-auth-brand-name">
                Salvation In Christ
              </Typography>

              <span className="jana-auth-brand-subtitle">
                SCHOOL SYSTEM - SICCS
              </span>
            </Box>
          </Box>

          <Box className="jana-auth-brand-content">
            <span className="jana-auth-brand-label">
              <VerifiedUser fontSize="small" />
              Secure Authentication
            </span>

            <Typography component="h1">
              Your Education.
              <br />
              Your Future.
              <br />
              <span>Protected.</span>
            </Typography>

            <Typography component="p">
              One final step helps protect your account and keeps
              your school information secure. Verify your identity
              to continue to your personalized school portal.
            </Typography>

            <Box className="jana-auth-security-list">
              <Box className="jana-auth-security-item">
                <Box className="jana-auth-security-icon">
                  <LockOutlined />
                </Box>

                <Box>
                  <strong>Secure Account Access</strong>
                  <span>
                    Verification helps protect your school account.
                  </span>
                </Box>
              </Box>

              <Box className="jana-auth-security-item">
                <Box className="jana-auth-security-icon">
                  <Email />
                </Box>

                <Box>
                  <strong>Email Verification</strong>
                  <span>
                    Use the code sent to your registered email.
                  </span>
                </Box>
              </Box>

              <Box className="jana-auth-security-item">
                <Box className="jana-auth-security-icon">
                  <ShieldOutlined />
                </Box>

                <Box>
                  <strong>Protected School Portal</strong>
                  <span>
                    Continue to your authorized dashboard after verification.
                  </span>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box className="jana-auth-brand-footer">
            Professional education. Strong values. Brighter futures.
            <br />
            © {new Date().getFullYear()} Salvation In Christ School System -SICSS.
          </Box>
        </Box>

        {/* Verification form */}
        <Box className="jana-auth-form-panel">
          <Box className="jana-auth-form-content">
            <Box className="jana-auth-mobile-brand">
              <Box className="jana-auth-brand-icon">
                <School />
              </Box>

              <Box>
                <Typography className="jana-auth-brand-name">
                  Salvation In Christ
                </Typography>

                <span className="jana-auth-brand-subtitle">
                  Educational Foundation
                </span>
              </Box>
            </Box>

            <Box className="jana-auth-step">
              <span className="jana-auth-step-dot" />
              ACCOUNT VERIFICATION
            </Box>

            <Typography component="h2" className="jana-auth-heading">
              Verify your login
            </Typography>

            <Typography className="jana-auth-description">
              Enter the authentication code sent to your email address
              to securely access your account.
            </Typography>

            <Box className="jana-auth-email">
              <Box className="jana-auth-email-icon">
                <Email />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <span className="jana-auth-email-label">
                  Verification email
                </span>

                <span className="jana-auth-email-value">
                  {email || "Email address unavailable"}
                </span>
              </Box>
            </Box>

            <form onSubmit={verifyCode}>
              <label className="jana-auth-code-label" htmlFor="auth-code">
                Authentication code
              </label>

              <TextField
                id="auth-code"
                className="jana-auth-code-input"
                fullWidth
                placeholder="Enter your code"
                variant="outlined"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  if (message) setMessage("");
                }}
                required
                autoComplete="one-time-code"
                disabled={loading || !email}
                inputProps={{
                  "aria-label": "Authentication code",
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VerifiedUser
                        sx={{ color: "#8056ad", fontSize: 21 }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        tabIndex={-1}
                        aria-label="Authentication code field"
                        disableRipple
                        sx={{
                          color: "#8056ad",
                          cursor: "default",
                          "&:hover": { background: "transparent" },
                        }}
                      >
                        <LockOutlined fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2.5 }}
              />

              {message && (
                <Alert
                  severity="error"
                  onClose={() => setMessage("")}
                  sx={{
                    mb: 2.5,
                    borderRadius: "12px",
                    fontSize: "12px",
                    alignItems: "center",
                    "& .MuiAlert-message": {
                      overflowWrap: "anywhere",
                    },
                  }}
                >
                  {message}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                disabled={loading || !email || !code.trim()}
                variant="contained"
                className="jana-auth-primary-button"
                endIcon={!loading ? <ArrowForward /> : null}
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={19}
                      sx={{ color: "#ffffff", mr: 1.2 }}
                    />
                    Verifying your account...
                  </>
                ) : (
                  "Verify & Continue"
                )}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                disabled={loading}
                onClick={() => navigate("/login")}
                startIcon={<ArrowBack />}
                className="jana-auth-back-button"
              >
                Back to Login
              </Button>
            </form>

            <Divider sx={{ mt: 3, borderColor: "#f0eaf6" }} />

            <Box className="jana-auth-privacy">
              <ShieldOutlined />
              Your verification details are used to authenticate your account.
            </Box>
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
};

export default LoginAuthentication;
